import React, { useMemo } from 'react';
import { ChevronLeft } from 'shared/assets/icons';
import { Collapse } from 'shared/components/ui/collapse';
import { IconButton } from 'shared/components/ui/icon-button';
import { Sizes, Variants } from 'shared/components/ui/icon-button/types';
import {
  PopupMenuCheckbox,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useToggle } from 'shared/hooks/use-toggle';

import * as Styled from './nested-option-item.style';
import { Option } from './types';
import { getAllDescendantValues } from './utils';

export interface NestedOptionItemProps {
  option: Option;
  selectedValues: string[];
  onChange: (option: Option) => void;
  level?: number;
  compact?: boolean;
}

export const NestedOptionItem: React.FC<NestedOptionItemProps> = ({
  option,
  selectedValues,
  onChange,
  level = 0,
  compact,
}) => {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const hasChildren = !!option.children?.length;
  const hasAllDescendantsSelected = useMemo(() => {
    const descendantValues = option.selectChildrenOnly
      ? getAllDescendantValues(option)
      : [];

    return (
      option.selectChildrenOnly &&
      descendantValues.length &&
      descendantValues.every((value) => selectedValues.includes(value))
    );
  }, [option, selectedValues]);

  const checked = option.selectChildrenOnly
    ? hasAllDescendantsSelected
    : selectedValues.includes(option.value);

  const handleCheckboxChange = () => {
    onChange(option);
  };

  const handleChevronClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    toggleExpanded();
  };

  return (
    <>
      <PopupMenuListItem>
        <Styled.OptionContainer>
          <PopupMenuCheckbox
            label={option.label}
            onChange={handleCheckboxChange}
            checked={!!checked}
            level={level}
            compact={compact}
          />
          {hasChildren && (
            <IconButton
              icon={ChevronLeft}
              rotation={isExpanded ? -90 : 0}
              onClick={handleChevronClick}
              size={Sizes.Small}
              variant={Variants.Contained}
              noHover
            />
          )}
        </Styled.OptionContainer>
      </PopupMenuListItem>

      {hasChildren && (
        <Collapse isOpen={isExpanded}>
          <Styled.ChildrenContainer>
            {option.children?.map((child) => (
              <NestedOptionItem
                key={child.value}
                option={child}
                selectedValues={selectedValues}
                onChange={onChange}
                level={level + 1}
                compact={compact}
              />
            ))}
          </Styled.ChildrenContainer>
        </Collapse>
      )}
    </>
  );
};
